<template>
  <div class="container mt-4">
    <!-- État de chargement -->
    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>

    <!-- Erreur -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Contenu de la carte -->
    <div v-else class="card mb-4 shadow-sm">
      <div class="row g-0">
        <!-- Image Pixel Art -->
        <div class="col-md-4 d-flex align-items-center justify-content-center bg-light pixel-art-container">
          <img :src="mosaic.image" alt="Pixel Art" class="pixel-art-img" />
        </div>
        
        <!-- Détails -->
        <div class="col-md-8">
          <div class="card-body">
            <!-- Titre avec bouton modifier à côté -->
            <div class="d-flex align-items-center gap-2 mb-3">
              <h5 class="card-title mb-0">Nom de la mosaïque</h5>
              <button 
                v-if="!editingName" 
                @click="startEditName" 
                class="btn btn-sm btn-outline-primary"
              >
                ✏️ Modifier
              </button>
            </div>
            
            <!-- Affichage normal du nom -->
            <p v-if="!editingName" class="card-text">
              <strong>{{ mosaic.name }}</strong>
            </p>
            
            <!-- Mode édition du nom -->
            <div v-else class="mb-3">
              <input 
                v-model="newName" 
                type="text" 
                class="form-control mb-2" 
                placeholder="Nom de la mosaïque"
                @keyup.enter="saveName"
              />
              <div class="d-flex gap-2">
                <button @click="saveName" class="btn btn-sm btn-success" :disabled="savingName">
                  <span v-if="savingName" class="spinner-border spinner-border-sm" role="status"></span>
                  <span v-else>✓ Valider</span>
                </button>
                <button @click="cancelEditName" class="btn btn-sm btn-secondary">
                  ✕ Annuler
                </button>
              </div>
            </div>
            
            <!-- Titre Description avec bouton modifier -->
            <div class="d-flex align-items-center gap-2 mt-3">
              <h6 class="mb-0">Description</h6>
              <button 
                v-if="!editingDescription" 
                @click="startEditDescription" 
                class="btn btn-sm btn-outline-primary"
              >
                ✏️ Modifier
              </button>
            </div>
            
            <!-- Affichage normal de la description -->
            <p v-if="!editingDescription" class="card-text mt-2">
              {{ mosaic.content || 'Aucune description disponible.' }}
            </p>
            
            <!-- Mode édition de la description -->
            <div v-else class="mt-2 mb-3">
              <textarea 
                v-model="newContent" 
                class="form-control mb-2" 
                rows="3"
                placeholder="Description de la mosaïque"
              ></textarea>
              <div class="d-flex gap-2">
                <button @click="saveDescription" class="btn btn-sm btn-success" :disabled="savingDescription">
                  <span v-if="savingDescription" class="spinner-border spinner-border-sm" role="status"></span>
                  <span v-else>✓ Valider</span>
                </button>
                <button @click="cancelEditDescription" class="btn btn-sm btn-secondary">
                  ✕ Annuler
                </button>
              </div>
            </div>

            <!-- Visibilité avec bouton modifier -->
            <div class="d-flex align-items-center gap-2 mt-3">
              <h6 class="mb-0">Visibilité</h6>
              <button 
                v-if="!editingVisibility" 
                @click="startEditVisibility" 
                class="btn btn-sm btn-outline-primary"
              >
                ✏️ Modifier
              </button>
            </div>
            
            <!-- Affichage normal de la visibilité -->
            <p v-if="!editingVisibility" class="card-text mt-2">
              <span v-if="mosaic.visibility" class="badge bg-success">Publique</span>
              <span v-else class="badge bg-secondary">Privée</span>
            </p>
            
            <!-- Mode édition de la visibilité -->
            <div v-else class="mt-2 mb-3">
              <div class="form-check">
                <input 
                  type="checkbox" 
                  class="form-check-input" 
                  id="visibilityCheck"
                  v-model="newVisibility"
                />
                <label class="form-check-label" for="visibilityCheck">
                  Rendre publique (visible par tous)
                </label>
              </div>
              <div class="d-flex gap-2 mt-2">
                <button @click="saveVisibility" class="btn btn-sm btn-success" :disabled="savingVisibility">
                  <span v-if="savingVisibility" class="spinner-border spinner-border-sm" role="status"></span>
                  <span v-else>✓ Valider</span>
                </button>
                <button @click="cancelEditVisibility" class="btn btn-sm btn-secondary">
                  ✕ Annuler
                </button>
              </div>
            </div>
            
            <p class="card-text mt-3">
              <small class="text-muted">Créée le {{ mosaic.creationDate }}</small>
            </p>
            
            <!-- Boutons d'action principale -->
            <div class="btn-group mt-3 d-block" role="group">
              <button @click="openEditor" class="btn btn-primary">
                ✏️ Modifier
              </button>
              <button @click="deleteMosaic" class="btn btn-danger" :disabled="deleting">
                <span v-if="deleting" class="spinner-border spinner-border-sm" role="status"></span>
                <span v-else>🗑️ Supprimer</span>
              </button>
            </div>

            <!-- Boutons PDF (nouvelle ligne) -->
            <div class="btn-group mt-2 d-block" role="group">
              <button @click="generatePDF" class="btn btn-success" :disabled="generatingPDF">
                <span v-if="generatingPDF" class="spinner-border spinner-border-sm" role="status"></span>
                <span v-else>{{ mosaic.pdfGenerated ? '🔄 Régénérer PDF' : '📄 Générer PDF' }}</span>
              </button>
              <button v-if="mosaic.pdfGenerated" @click="downloadPDF" class="btn btn-info">
                📥 Télécharger PDF
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Commentaires (en dur) -->
      <div class="card-footer bg-white border-top">
        <h6 class="mb-2">💬 Commentaires</h6>
        <div class="mb-2">
          <strong>Alice :</strong> Magnifique style rétro !
        </div>
        <div class="mb-2">
          <strong>Bob :</strong> On dirait un jeu SNES, j'adore 🔥
        </div>
        <div>
          <strong>Charlie :</strong> Tellement inspirant pour mes propres créations pixel 🎨
        </div>
      </div>
    </div>

    <!-- Modal de progression PDF -->
    <div class="modal fade" ref="progressModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Génération du PDF en cours</h5>
          </div>
          <div class="modal-body">
            <div class="progress" role="progressbar">
              <div class="progress-bar" :style="{ width: progressValue + '%' }">
                {{ progressValue }}%
              </div>
            </div>
            <p class="mt-2 text-center text-muted">{{ progressMessage }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as bootstrap from 'bootstrap';

export default {
  name: 'PixelArtCard',
  data() {
    return {
      mosaic: null,
      loading: true,
      error: null,
      deleting: false,
      editingName: false,
      newName: '',
      savingName: false,
      editingDescription: false,
      newContent: '',
      savingDescription: false,
      editingVisibility: false,
      newVisibility: false,
      savingVisibility: false,
      generatingPDF: false,
      progressValue: 0,
      progressMessage: '',
      es: null,
      modalInstance: null
    };
  },
  mounted() {
    this.fetchMosaic();
    const modalElement = this.$refs.progressModal;
    this.modalInstance = new bootstrap.Modal(modalElement);
  },
  beforeUnmount() {
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    if (this.modalInstance) {
      this.modalInstance.dispose();
    }
  },
  methods: {
    async fetchMosaic() {
      try {
        const token = localStorage.getItem('token');
        const mosaicId = this.$route.params.id;
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        const response = await axios.get(`${API_URL}/api/mosaics/liste`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        this.mosaic = response.data.find(m => m.id === parseInt(mosaicId));
        
        if (!this.mosaic) {
          this.error = 'Mosaïque non trouvée';
        }
        
        this.loading = false;
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        this.error = 'Impossible de charger la mosaïque';
        this.loading = false;
      }
    },
    
    startEditName() {
      this.newName = this.mosaic.name;
      this.editingName = true;
    },
    
    cancelEditName() {
      this.editingName = false;
      this.newName = '';
    },
    
    async saveName() {
      if (!this.newName.trim()) {
        alert('Le nom ne peut pas être vide');
        return;
      }
      
      this.savingName = true;
      
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        await axios.post(`${API_URL}/api/mosaic/${this.mosaic.id}/update`, {
          name: this.newName
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        this.mosaic.name = this.newName;
        this.editingName = false;
        this.newName = '';
        
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        alert('Impossible de modifier le nom');
      } finally {
        this.savingName = false;
      }
    },
    
    startEditDescription() {
      this.newContent = this.mosaic.content || '';
      this.editingDescription = true;
    },
    
    cancelEditDescription() {
      this.editingDescription = false;
      this.newContent = '';
    },
    
    async saveDescription() {
      this.savingDescription = true;
      
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        await axios.post(`${API_URL}/api/mosaic/${this.mosaic.id}/update`, {
          content: this.newContent
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        this.mosaic.content = this.newContent;
        this.editingDescription = false;
        this.newContent = '';
        
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        alert('Impossible de modifier la description');
      } finally {
        this.savingDescription = false;
      }
    },

    startEditVisibility() {
      this.newVisibility = this.mosaic.visibility;
      this.editingVisibility = true;
    },
    
    cancelEditVisibility() {
      this.editingVisibility = false;
      this.newVisibility = false;
    },
    
    async saveVisibility() {
      this.savingVisibility = true;
      
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        await axios.post(`${API_URL}/api/mosaic/${this.mosaic.id}/update`, {
          visibility: this.newVisibility
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        this.mosaic.visibility = this.newVisibility;
        this.editingVisibility = false;
        this.newVisibility = false;
        
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        alert('Impossible de modifier la visibilité');
      } finally {
        this.savingVisibility = false;
      }
    },
    
    generatePDF() {
      if (this.es) { 
        this.es.close(); 
        this.es = null; 
      }
    
      const jwt = localStorage.getItem('token');

      if (!jwt) {
        console.error('❌ Pas de token JWT');
        alert('Vous devez être connecté pour générer le PDF');
        return;
      }
      const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
      const url = `${API_URL}/api/creatMosaicPDF/${this.mosaic.id}/stream?bearer=${encodeURIComponent(jwt)}`;
      console.log('🌐 Connexion SSE à:', url);
    
      this.generatingPDF = true;
      this.progressValue = 0;
      this.progressMessage = 'Préparation...';
      this.modalInstance.show();
    
      try {
        this.es = new EventSource(url);
      
        this.es.addEventListener('started', (e) => {
          console.log('📡 SSE started:', e.data);
          try {
            const data = JSON.parse(e.data || '{}');
            this.progressValue = typeof data.percent === 'number' ? data.percent : 0;
            this.progressMessage = data.message || 'Démarrage...';
          } catch (err) {
            console.error('Erreur parsing started:', err);
          }
        });
      
        this.es.addEventListener('progress', (e) => {
          console.log('📊 SSE progress:', e.data);
          try {
            const data = JSON.parse(e.data || '{}');
            if (typeof data.percent === 'number') {
              this.progressValue = data.percent;
            }
            if (data.message) {
              this.progressMessage = data.message;
            }
          } catch (err) {
            console.error('Erreur parsing progress:', err);
          }
        });
      
        this.es.addEventListener('done', (e) => {
          console.log('✅ SSE done:', e.data);
          try {
            this.progressValue = 100;
            this.progressMessage = 'PDF généré avec succès !';
            
            this.mosaic.pdfGenerated = true;
            
            setTimeout(() => {
              this.modalInstance.hide();
              this.generatingPDF = false;
            }, 1500);
            
          } catch (err) {
            console.error('Erreur parsing done:', err);
          } finally {
            this.es?.close();
            this.es = null;
          }
        });
      
        this.es.addEventListener('error', (e) => {
          console.error('❌ SSE error event:', e);
          if (e.data) {
            try {
              const data = JSON.parse(e.data);
              alert('Erreur: ' + data.message);
            } catch (err) {
              console.error('Erreur parsing error:', err);
            }
          }
          this.modalInstance.hide();
          this.generatingPDF = false;
          this.es?.close();
          this.es = null;
        });
      
        this.es.onerror = (e) => {
          console.error('❌ EventSource onerror:', e);
          if (this.es && this.es.readyState === EventSource.CLOSED) {
            alert('Erreur de connexion au serveur');
            this.modalInstance.hide();
            this.generatingPDF = false;
            this.es.close();
            this.es = null;
          }
        };
      
      } catch (err) {
        console.error('❌ Erreur création EventSource:', err);
        alert('Impossible de se connecter au serveur: ' + err.message);
        this.modalInstance.hide();
        this.generatingPDF = false;
      }
    },
    
    async downloadPDF() {
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        const response = await fetch(`${API_URL}/api/pdf/mosaic/${this.mosaic.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Erreur lors du téléchargement du PDF');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `mosaic-${this.mosaic.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        console.error('Erreur téléchargement PDF:', error);
        alert('Impossible de télécharger le PDF');
      }
    },
    
    openEditor() {
      this.$router.push({
        name: 'MosaicConcepteur',
        query: { mosaicId: this.mosaic.id }
      });
    },
    
    async deleteMosaic() {
      if (!confirm('Êtes-vous sûr de vouloir supprimer cette mosaïque ?')) {
        return;
      }
      
      this.deleting = true;
      
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        await axios.delete(`${API_URL}/api/mosaic/suprime/${this.mosaic.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        alert('Mosaïque supprimée avec succès !');
        this.$router.push('/');
        
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Impossible de supprimer la mosaïque');
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.pixel-art-container {
  min-height: 400px;
  padding: 20px;
}

.pixel-art-img {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 500px;
  object-fit: contain;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  -ms-interpolation-mode: nearest-neighbor;
}

.card-footer {
  padding: 1rem 1.25rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.gap-2 {
  gap: 0.5rem;
}

@media (min-width: 768px) {
  .pixel-art-container {
    min-height: 500px;
  }
  
  .pixel-art-img {
    max-height: 600px;
  }
}

@media (min-width: 1200px) {
  .pixel-art-container {
    min-height: 600px;
  }
  
  .pixel-art-img {
    max-height: 700px;
  }
}
</style>